/*
 * Copyright (c) 2019 - Indigen Solutions
 * Authors:
 *   - Bichoy Messiha <bm@indigen.com>
 * NOTICE: All information contained herein is, and remains
 * the property of Indigen Solutions and its suppliers,
 * if any.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Indigen Solutions.
 */
import { WithStyles } from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";
import * as moment from "moment";
import * as React from "react";

import { getProductHelper } from "../../helper/functions.helper";
import { ProductHelper } from "../../helper/Product.helper";
import { ProductAAHelper } from "../../helper/ProductAA.helper";
import { ProductGOHelper } from "../../helper/ProductGO.helper";
import { ProductGPIHelper } from "../../helper/ProductGPI.helper";
import { ProductSPRHelper } from "../../helper/ProductSPR.helper";
import { Customer } from "../../types/Customer";
import { NotificationItem } from "../../types/NotificationItem";
import { ProductName, Products, ProductVariantName } from "../../types/Products";
import { Quotation } from "../../types/Quotation";
import Notification from "../Notification/Notification";
import QuotationFormProductAA from "../QuotationForm/QuotationFormProductAA";
import QuotationFormProductGO from "../QuotationForm/QuotationFormProductGO";
import QuotationFormProductGPI from "../QuotationForm/QuotationFormProductGPI";
import QuotationFormProductSPR from "../QuotationForm/QuotationFormProductSPR";
import CalculatorPopinFormCustomer from "./CalculatorPopinFormCustomer";
import { calculatorPopinFormStyles } from "./CalculatorPopinForm.styles";
import { User } from "../../models/User.model";

interface CalculatorPopinFormProps extends WithStyles<typeof calculatorPopinFormStyles> {
    user: User;
    productName: ProductName;
    productVariantName: ProductVariantName;
    onValidate?: (customer: Customer, quotation: Quotation, isCustomerModifiedOrNew?: boolean) => void;
}

type CalculatorPopinFormState = {
    customer: Customer;
    quotation: Quotation;
    isCustomerModifiedOrNew?: boolean;
    notification: NotificationItem;
};

class CalculatorPopinForm extends React.Component<CalculatorPopinFormProps, CalculatorPopinFormState> {
    private productHelper: ProductHelper = null;

    public async componentWillMount() {
        await this.init();
    }

    public onCustomerChange(customer: Customer, isCustomerModifiedOrNew?: boolean) {
        this.setState({ customer, isCustomerModifiedOrNew });
        this.productHelper.setCustomer(customer);
    }

    public onDateChange(effectiveDate: string) {
        const quotation = { ...this.state.quotation, effectiveDate };
        this.setState({ quotation });
        this.productHelper.setQuotation(quotation);
    }

    public onProductChange(product: Products) {
        const quotation = { ...this.state.quotation, product: product };
        this.setState({ quotation });
        this.productHelper.setQuotation(quotation);
    }

    public async onSubmit(event) {
        event.preventDefault();
        this.props.onValidate(this.state.customer, this.state.quotation, this.state.isCustomerModifiedOrNew);
    }

    public renderProductForm() {
        switch (this.props.productName) {
            case "AA":
                return (
                    <QuotationFormProductAA
                        mode="calculator"
                        classes={this.props.classes}
                        productHelper={this.productHelper as ProductAAHelper}
                        quotation={this.state.quotation}
                        customer={this.state.customer}
                        onChange={this.onProductChange.bind(this)}
                        showError={this.showError.bind(this)}
                        hideError={this.hideError.bind(this)}
                    />
                );
            case "SPR":
                return (
                    <QuotationFormProductSPR
                        mode="calculator"
                        classes={this.props.classes}
                        productHelper={this.productHelper as ProductSPRHelper}
                        quotation={this.state.quotation}
                        customer={this.state.customer}
                        onChange={this.onProductChange.bind(this)}
                        showError={this.showError.bind(this)}
                        hideError={this.hideError.bind(this)}
                    />
                );
            case "GO":
                return (
                    <QuotationFormProductGO
                        mode="calculator"
                        classes={this.props.classes}
                        productHelper={this.productHelper as ProductGOHelper}
                        productVariantName={this.props.productVariantName}
                        quotation={this.state.quotation}
                        customer={this.state.customer}
                        onChange={this.onProductChange.bind(this)}
                        showError={this.showError.bind(this)}
                        hideError={this.hideError.bind(this)}
                    />
                );
            case "GPI":
                return (
                    <QuotationFormProductGPI
                        mode="calculator"
                        classes={this.props.classes}
                        productHelper={this.productHelper as ProductGPIHelper}
                        quotation={this.state.quotation}
                        customer={this.state.customer}
                        onChange={this.onProductChange.bind(this)}
                        showError={this.showError.bind(this)}
                        hideError={this.hideError.bind(this)}
                    />
                );
            default:
                return null;
        }
    }

    public render() {
        if (!this.state) return null;

        return (
            <div>
                {this.state.notification ? (
                    <Notification
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        transitionDuration={0}
                        autoHideDuration={0}
                        notification={this.state.notification}
                    />
                ) : null}
                <form onSubmit={this.onSubmit.bind(this)}>
                    <CalculatorPopinFormCustomer
                        classes={this.props.classes}
                        customer={this.state.customer}
                        productName={this.props.productName}
                        onChange={this.onCustomerChange.bind(this)}
                    />
                    {this.renderProductForm()}
                </form>
            </div>
        );
    }

    private showError(message: string) {
        if (!this.state.notification) {
            this.setState({
                notification: { message: message, code: "400", variant: "error" },
            });
        }
    }

    private hideError() {
        if (this.state.notification) {
            this.setState({
                notification: null,
            });
        }
    }

    private async init() {
        this.productHelper = getProductHelper(this.props.productName, this.props.productVariantName);
        const quotation = Quotation.createNew({
            validityDate: moment()
                // .add(1, "month")
                .format("YYYY-MM-DD"),
            effectiveDate: moment()
                .add(1, "month")
                .format("YYYY-MM-01"),
            creationDate: moment().toISOString(),
            productName: this.props.productName,
            productVariantName: this.props.productVariantName,
            product: (this.productHelper as any).defaultValue,
            actionHistory: [
                {
                    date: moment().toISOString(),
                    action: "creation",
                    type: "proposition",
                    documents: [],
                },
            ],
        });

        const customer = Customer.createNew({
            birthdate: this.getMaxDateForProduct(this.props.productName),
            quotations: {},
        });

        await this.productHelper.init(this.props.user, customer, quotation);

        this.setState({ quotation, customer });
    }

    private getMaxDateForProduct(productName: ProductName) {
        if (productName === "GPI")
            return moment()
                .subtract(18, "year")
                .date(31)
                .month(11)
                .format("YYYY-MM-DD");
        return moment()
            .subtract(18, "year")
            .format("YYYY-MM-DD");
    }
}

export default withStyles(calculatorPopinFormStyles)(CalculatorPopinForm);



// WEBPACK FOOTER //
// ./src/components/Calculator/CalculatorPopinForm.tsx